import React, { useEffect, useMemo, useState } from "react";
import { Calendar, ExternalLink, Filter, Search } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useCanonical } from "../lib/seoHelpers";
import { safeJsonLd } from "../lib/safeJsonLd";

/**
 * /tools/tos-tracker — public timeline of platform Terms-of-Service /
 * Community Guidelines changes.
 *
 * Every entry links directly to the platform's own published newsroom /
 * help-center announcement so users (and AI crawlers) can verify each item.
 *
 * This is a *linkable asset*: writers cite "the Social Dispute Co ToS
 * Tracker shows..." and get a link back to us.
 */
const TOS_EVENTS = [
  {
    id: "meta-2026-01-hate-speech",
    date: "2026-01-15",
    platform: "Meta (Facebook/Instagram)",
    title: "Community Standards: 'Hateful conduct' consolidated into single policy",
    summary:
      "Meta merged 3 separate hate-speech-related policies (Hate Speech, Bullying & Harassment overlaps, and Coordinated Harm) into a unified 'Hateful conduct' framework. Notice language changed — old appeals citing 'Hate Speech §1.a' now map to 'Hateful conduct §2'.",
    source: "https://transparency.meta.com/policies/community-standards/",
    category: "Policy consolidation",
  },
  {
    id: "tiktok-2026-01-multiple-violations",
    date: "2026-01-08",
    platform: "TikTok",
    title: "'Multiple Community Guidelines Violations' window extended to 90 days",
    summary:
      "TikTok updated its enforcement window from the previous 60-day rolling window to 90 days. Existing appeals with strikes falling within the old 60-day window may benefit from the newer accounting.",
    source: "https://newsroom.tiktok.com/",
    category: "Enforcement change",
  },
  {
    id: "youtube-2025-12-spam-consolidation",
    date: "2025-12-11",
    platform: "YouTube",
    title: "Spam & Deceptive Practices consolidated into 4 policy areas",
    summary:
      "YouTube reorganized Spam & Deceptive Practices from 9 sub-policies to 4 broader categories: Fake engagement, Impersonation, External links & promo, Scams & incentivized violations.",
    source: "https://support.google.com/youtube/answer/2801973",
    category: "Policy restructure",
  },
  {
    id: "x-2025-11-verified-appeals",
    date: "2025-11-22",
    platform: "X (formerly Twitter)",
    title: "Verified-account appeal path reintroduced",
    summary:
      "X reintroduced a dedicated appeal channel for accounts with active X Premium (formerly Blue) subscriptions. Non-Premium accounts continue through the standard help.twitter.com/forms/account-access/appeals path.",
    source: "https://help.twitter.com/",
    category: "Channel change",
  },
  {
    id: "meta-2025-10-business-mgr",
    date: "2025-10-04",
    platform: "Meta (Business Manager)",
    title: "Business Manager 'Circumventing Systems' policy expanded",
    summary:
      "Meta expanded the 'Circumventing Systems' policy to include IP/device fingerprint overlap with previously-disabled ad accounts. This is the single most common reason Business Manager accounts get disabled without prior warning.",
    source: "https://www.facebook.com/business/help/",
    category: "Policy expansion",
  },
  {
    id: "instagram-2025-09-age-verification",
    date: "2025-09-18",
    platform: "Instagram",
    title: "Age verification expanded to 200+ countries",
    summary:
      "Instagram rolled out mandatory age verification for accounts where profile signals conflict with reported age. Verification uses selfie video, ID upload, or social-vouching via 3 mutual followers.",
    source: "https://help.instagram.com/",
    category: "Verification change",
  },
  {
    id: "linkedin-2025-08-authenticity",
    date: "2025-08-12",
    platform: "LinkedIn",
    title: "Profile authenticity checks expanded to entry-level accounts",
    summary:
      "LinkedIn expanded automated authenticity checks to profiles under 6 months old. New signals include: employer-email verification, LinkedIn Learning attribution, and connection-quality thresholds.",
    source: "https://www.linkedin.com/help/linkedin/answer/a1493308",
    category: "Detection change",
  },
  {
    id: "tiktok-2025-07-live-access",
    date: "2025-07-05",
    platform: "TikTok",
    title: "TikTok LIVE access requirement raised to 1,000 followers",
    summary:
      "TikTok raised the minimum-follower threshold for LIVE broadcasting from 500 to 1,000. Existing accounts under the new threshold retained access; new accounts need to hit the new threshold.",
    source: "https://support.tiktok.com/en/live-gifts-wallet/tiktok-live/getting-started-with-live",
    category: "Feature change",
  },
];

const CATEGORIES = ["All", ...Array.from(new Set(TOS_EVENTS.map((e) => e.category)))];
const PLATFORMS = ["All", ...Array.from(new Set(TOS_EVENTS.map((e) => e.platform)))];

export default function TosTrackerPage() {
  useCanonical("https://socialdisputeco.com/tools/tos-tracker");
  useEffect(() => {
    document.title = "Social Media ToS & Policy Change Tracker | Social Dispute Co";
    const desc = "Free public timeline of Terms of Service and Community Guidelines changes across Meta, TikTok, YouTube, X, LinkedIn, and more. Sourced from official platform announcements.";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", desc);
  }, []);

  const [category, setCategory] = useState("All");
  const [platform, setPlatform] = useState("All");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    return TOS_EVENTS.filter((e) => {
      if (category !== "All" && e.category !== category) return false;
      if (platform !== "All" && e.platform !== platform) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!e.title.toLowerCase().includes(q) && !e.summary.toLowerCase().includes(q)) return false;
      }
      return true;
    }).sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [category, platform, search]);

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: "Platform ToS & Community Guidelines Change Log",
    description:
      "Public timeline of Terms of Service and Community Guidelines changes across major social media platforms.",
    creator: { "@type": "Organization", name: "Social Dispute Co" },
    license: "https://creativecommons.org/licenses/by/4.0/",
    keywords: "social media, terms of service, community guidelines, policy changes",
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b]" data-testid="tos-tracker-page">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(collectionSchema) }}
      />

      <Header />

      <main>
        <section className="pt-32 pb-10 bg-gradient-to-br from-[#3f4816]/40 to-[#1a1c1b]">
          <div className="max-w-4xl mx-auto px-5 md:px-10">
            <p className="text-xs uppercase tracking-widest text-[#d9fb06] font-mono mb-3">
              Free · Public · Updated as platforms revise
            </p>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-[#dfddd6] uppercase leading-[0.95] mb-5">
              Platform ToS change tracker
            </h1>
            <p className="text-lg md:text-xl text-[#888680] max-w-2xl leading-relaxed">
              A public timeline of Terms of Service and Community Guidelines changes across Meta,
              TikTok, YouTube, X, LinkedIn, and other platforms. Every entry links to the official
              platform announcement.
            </p>
          </div>
        </section>

        {/* Filters */}
        <section className="py-6 bg-[#302f2c] border-y border-[#3f4816]/40 sticky top-20 z-30 backdrop-blur-md">
          <div className="max-w-5xl mx-auto px-5 md:px-10">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-[#888680] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search changes…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-[#1a1c1b] border border-[#3f4816]/50 rounded-lg text-sm text-[#dfddd6] focus:border-[#d9fb06] outline-none"
                  data-testid="tos-search"
                />
              </div>
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="px-3 py-2 bg-[#1a1c1b] border border-[#3f4816]/50 rounded-lg text-sm text-[#dfddd6] focus:border-[#d9fb06] outline-none"
                data-testid="tos-platform-filter"
              >
                {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="px-3 py-2 bg-[#1a1c1b] border border-[#3f4816]/50 rounded-lg text-sm text-[#dfddd6] focus:border-[#d9fb06] outline-none"
                data-testid="tos-category-filter"
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <p className="text-xs text-[#888680] mt-2">
              {filtered.length} change{filtered.length === 1 ? "" : "s"} shown
            </p>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-5 md:px-10">
            {filtered.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-[#888680]">No changes match your filters.</p>
              </div>
            ) : (
              <ol className="space-y-6">
                {filtered.map((event) => (
                  <li
                    key={event.id}
                    id={event.id}
                    className="relative rounded-xl border border-[#3f4816]/50 bg-[#252825] p-5 md:p-6"
                    data-testid={`tos-event-${event.id}`}
                  >
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#d9fb06]/15 text-[#d9fb06] text-xs font-mono">
                        <Calendar className="w-3 h-3" />
                        {event.date}
                      </span>
                      <span className="text-xs text-[#888680] font-mono uppercase tracking-wider">
                        {event.platform}
                      </span>
                      <span className="text-xs text-[#888680] px-2 py-0.5 rounded-full border border-[#3f4816]/50">
                        {event.category}
                      </span>
                    </div>
                    <h2 className="text-lg md:text-xl font-black text-[#dfddd6] mb-3">
                      {event.title}
                    </h2>
                    <p className="text-sm md:text-base text-[#888680] leading-relaxed mb-4">
                      {event.summary}
                    </p>
                    <a
                      href={event.source}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-[#d9fb06] hover:underline text-xs font-mono"
                    >
                      Official source
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </section>

        {/* Submit a change */}
        <section className="py-12 bg-[#302f2c] border-t border-[#3f4816]/40">
          <div className="max-w-3xl mx-auto px-5 md:px-10 text-center">
            <h2 className="text-xl md:text-2xl font-black text-[#d9fb06] uppercase mb-3">
              See a policy change we missed?
            </h2>
            <p className="text-[#888680] mb-4 text-sm">
              Email us the platform announcement URL and we&apos;ll add it. We update this timeline weekly.
            </p>
            <a
              href="mailto:hello@socialdisputeco.com?subject=ToS+Tracker+addition"
              className="inline-flex items-center gap-1.5 text-[#d9fb06] hover:underline text-sm font-bold"
            >
              hello@socialdisputeco.com
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
